import { computed } from 'vue'
import { useCookie } from 'nuxt/app'
import { apiService } from '~/services/api'
// import { IFRAME_ENDPOINT } from '~~/constant/iframe.constant'

export function useAuth() {
    const config = useRuntimeConfig()
    const getAccessToken = () => useCookie<string | null>('at', { watch: false }).value
    const getRefreshToken = () => useCookie<string | null>('rt', { watch: false }).value
    const getStoreCode = () => useCookie<string | null>('sc', { watch: false }).value
    const getMdsf = () => useCookie<string | null>('mdsf', { watch: false }).value
    const getDs = () => useCookie<boolean | null>('dsf', { watch: false }).value

    const isLoggedIn = computed(() => {
        const token = getAccessToken()
        return !!token && isTokenValid(token)
    })

    function parseJwt(token: string): any {
        try {
            const payload = token.split('.')[1]
            return JSON.parse(atob(payload))
        } catch {
            return null
        }
    }

    function isTokenValid(token: string | null): boolean {
        if (!token) { return false }
        const payload = parseJwt(token)
        return payload?.exp && payload.exp > Math.floor(Date.now() / 1000)
    }

    function getTokenData() {
        const token = getAccessToken()
        return token ? parseJwt(token) : null
    }

    async function login(nik: string, pin: string): Promise<{ success: boolean, error?: string }> {
        const { data, error } = await apiService.auth.login({ nik, pin })

        if (error || !data) {
            return { success: false, error: error ?? 'Login gagal' }
        }

        const response = data
        // Set cookie
        const at = useCookie('at', { maxAge: response.data.tokenExp, path: '/', secure: true, sameSite: 'lax', httpOnly: false })
        const rt = useCookie('rt', { maxAge: response.data.refreshTokenExp, path: '/', secure: true, sameSite: 'lax', httpOnly: false })
        const sc = useCookie('sc', { maxAge: response.data.tokenExp, path: '/', secure: true, sameSite: 'lax', httpOnly: false })
        const mdsf = useCookie('mdsf', { maxAge: response.data.tokenExp, path: '/', secure: true, sameSite: 'lax' })
        const dsf = useCookie<boolean | null>('dsf', { maxAge: response.data.tokenExp, path: '/', secure: true, sameSite: 'lax' })

        at.value = response.data.token
        rt.value = response.data.refreshToken
        sc.value = response.data.sc
        mdsf.value = String(response.data.mdsFlag)
        dsf.value = response.data.dsFlag
        return { success: true }
    }

    async function refreshAccessToken(): Promise<string | null> {
        const rtVal = getRefreshToken()
        if (!rtVal || !isTokenValid(rtVal)) { return null }

        const { data, error } = await apiService.auth.refresh(rtVal, 3)

        if (error || !data) {
            console.error('Gagal refresh token:', error)
            await logout()
            return null
        }

        const response = data
        useCookie('at', { maxAge: response.data.expiresIn }).value = response.data.token
        useCookie('rt').value = response.data.refreshToken

        return response.data.token
    }

    async function logout(): Promise<boolean> {
        const dashboardIframe = document.getElementById('my-iframe') as HTMLIFrameElement

        if (dashboardIframe && dashboardIframe.contentWindow) {
            const iframeUrl = config.public.IFRAME_URL as string
            const iframeOrigin = getOriginFromUrl(iframeUrl)
            dashboardIframe.contentWindow.postMessage({ type: 'LOGOUT' }, iframeOrigin)
        }

        const token = getAccessToken()
        const { nik } = parseJwt(token || '') ?? {}

        try {
            await apiService.auth.logout({ nik })
        } catch (error) {
            console.warn('Logout error, fallback to local clear', error)
        }

        // Clear cookies (client-side)
        useCookie('at').value = null
        useCookie('rt').value = null
        useCookie('sc').value = null
        useCookie('tracking-auth').value = null
        useCookie('dsf').value = null

        // navigateTo('/login')
        window.location.reload()

        return true
    }

    return {
        login,
        logout,
        refreshAccessToken,
        isLoggedIn,
        getAccessToken,
        getRefreshToken,
        getTokenData,
        getStoreCode,
        isTokenValid,
        getMdsf,
        getDs
    }
}
